import { type IMediator, Mediator } from '#/core/mediator';

import { CookieService } from '#/services/cookie';
import { VisibilityService } from '#/services/visibility';

import { type Instance } from '#/types/services';

import './index.styles.scss';
import { SERVICES_TYPES } from './services-types';
import { ApiService } from './services/api';
import { ChartStateStorageService } from './services/chart-state-storage';
import { type IChartWidgetService } from './services/chart-widget';
import { ConfigProviderService } from './services/config-provider';
import { DataFeedService } from './services/data-feed';
import { DeviceService } from './services/device';
import { EnvService } from './services/env';
import { I18nService } from './services/i18n';
import { IntervalBarUpdateService } from './services/interval-bar-update';
import { LogService } from './services/log';
import { MessageListenerService } from './services/message-listener';
import { NotificationService } from './services/notification';
import { RealtimeBarUpdateService } from './services/realtime-bar-update';
import { ServerTimeService } from './services/server-time';
import { StorageService } from './services/storage';
import { TemplateStorageService } from './services/template-storage';
import { ThemeService } from './services/theme';
import { UrlParamsService } from './services/url-params';
import { type IVirtualConsoleService, VirtualConsoleService } from './services/virtual-console';

interface ServiceType {
  new (mediator: IMediator): Instance;
}

const startApp = async () => {
  const mediator = new Mediator();

  const servicesMap = new Map<symbol, [ServiceType] | [ServiceType, (instance: Instance) => void | Promise<void>]>([
    [SERVICES_TYPES.EnvService, [EnvService]],
    [SERVICES_TYPES.UrlParamsService, [UrlParamsService]],
    [SERVICES_TYPES.CookieService, [CookieService]],
    [SERVICES_TYPES.SentryService, [(await import('./services/sentry')).SentryService]],
    [
      SERVICES_TYPES.VirtualConsoleService,
      [VirtualConsoleService, (instance) => (instance as IVirtualConsoleService).start()],
    ],
    [SERVICES_TYPES.LogService, [LogService]],
    [SERVICES_TYPES.DeviceService, [DeviceService]],
    [SERVICES_TYPES.ConfigProviderService, [ConfigProviderService]],
    [SERVICES_TYPES.StorageService, [StorageService]],
    [SERVICES_TYPES.ChartStateStorageService, [ChartStateStorageService]],
    [SERVICES_TYPES.ServerTimeService, [ServerTimeService]],
    [SERVICES_TYPES.ApiService, [ApiService]],
    [SERVICES_TYPES.I18nService, [I18nService]],
    [SERVICES_TYPES.ThemeService, [ThemeService]],
    [SERVICES_TYPES.NotificationService, [NotificationService]],
    [SERVICES_TYPES.MessageListenerService, [MessageListenerService]],
    [SERVICES_TYPES.IntervalBarUpdateService, [IntervalBarUpdateService]],
    [SERVICES_TYPES.RealtimeBarUpdateService, [RealtimeBarUpdateService]],
    [SERVICES_TYPES.VisibilityService, [VisibilityService]],
    [SERVICES_TYPES.DataFeedService, [DataFeedService]],
    [SERVICES_TYPES.TemplateStorageService, [TemplateStorageService]],
    [
      SERVICES_TYPES.ChartWidgetService,
      [
        (await import('./services/chart-widget')).ChartWidgetService,
        (instance) => (instance as IChartWidgetService).createChartWidget(),
      ],
    ],
  ]);

  for (const [uniqueId, [Service, cb]] of servicesMap.entries()) {
    const instance = new Service(mediator);

    mediator.registerSingleton(uniqueId, instance);

    await cb?.(instance);
  }
};

window.addEventListener('DOMContentLoaded', startApp, false);
